import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAuth } from '../../auth/AuthProvider';
import { fetchAppointments } from '../../services/appointments';
import { agendaTimezone, getAgendaBounds } from '../Agenda/agendaUtils';
import usePanelSize from '../../hooks/usePanelSize';
import { t } from '../../i18n';

export default function TodayAgenda() {
    const { user, role } = useAuth();
    const zone = agendaTimezone(user.timezone);
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(interval);
    }, []);
    const today = dayjs(now).tz(zone).format('YYYY-MM-DD');
    const frame = usePanelSize(96);
    const query = useQuery({
        queryKey: ['clinic-agenda-appointments', 'home-today', role.uuid, role.account.uuid, today, zone],
        queryFn: ({ signal }) => fetchAppointments({ user, role }, {
            ...getAgendaBounds({ start: today, end: today }, zone),
            statuses: ['SCHEDULED', 'ONGOING'], signal
        }),
        enabled: role.name === 'CLINIC_ADMIN', staleTime: 30000, refetchInterval: 60000, retry: 1
    });
    const rows = (query.data || []).filter((item) => item.status === 'ONGOING'
        || dayjs(item.ts).add(Number(item.slot_duration) || 30, 'minute').valueOf() > now)
        .sort((a, b) => (a.status === 'ONGOING' ? 0 : 1) - (b.status === 'ONGOING' ? 0 : 1)
            || Date.parse(a.ts) - Date.parse(b.ts));
    const count = Math.max(1, Math.min(3, Math.floor(frame.height / 44)));
    return <Stack sx={{ height: '100%', minHeight: 0 }} spacing={0.5}>
        <Typography component="h2" variant="h4" color="primary.main" sx={{ fontSize: { xs: 15, sm: 17 } }}>
            {t('Agenda de hoje')}
        </Typography>
        <Box ref={frame.ref} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {query.isPending ? <Skeleton height={42} /> : query.isError
                ? <Button size="small" onClick={() => query.refetch()}>{t('Tentar novamente')}</Button>
                : !rows.length ? <Typography variant="body2" color="text.secondary">{t('Sem próximos agendamentos hoje.')}</Typography>
                : rows.slice(0, count).map((item) => <Stack key={item.uuid} direction="row" alignItems="center" spacing={1.5}
                    sx={{ minHeight: 44 }}>
                    <Typography color="primary.main" fontWeight={700} sx={{ fontSize: 13, flexShrink: 0 }}>
                        {dayjs(item.ts).tz(zone).format('HH:mm')}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>{item.patient.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: 11 }}>
                            {item.doctor.name}
                        </Typography>
                    </Box>
                </Stack>)}
        </Box>
        <Button component={Link} to="/agenda" size="small" sx={{ alignSelf: 'flex-start', p: 0, minHeight: 24 }}>
            {t('Ver agenda')}
        </Button>
    </Stack>;
}
